import React, { useCallback, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import useSWR from 'swr'
import { Badge, Banner, ChevronIcon, ImageSlot, ToggleGroup, TrashIcon } from '../../../components'
import MatchSelect, { MATCH_LABEL_SWR_KEY, MATCH_SEARCH_HINT, fetchMatchLabel } from '../../MatchSelect'
import LinkRefSelector, {
  CHANNEL_PAGE_TYPE, EMPTY_LINK_REF, EXTERNAL_PAGE_TYPE, LinkRefValue, isExternalLinkRef, linkRefFromStored,
  storedPageType, validateLinkRef,
} from '../LinkRefSelector'
import { ScheduleFields } from '../ScheduleFields'
import { useWidgetSite } from '../../../contexts/WidgetSiteContext'
import { useDragReorder } from '../../../hooks'
import {
  PageTypeInfo, fetchArtworkTemplates, fetchMatchImages, fetchPageTypeConfig, uploadHeroImage,
} from '../../../services/api'
import { arrayMove } from '../../../utils/arrayMove'
import { formatScheduleRange, getScheduleStatus } from '../../../utils/merchandising'
import { convertPTInputToUTC, convertUTCToPTInput } from '../../../utils/timezone'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import styles from './itemListEditor.module.scss'

// Stored metadata shape — mirrors what ACE's hydrateHeroBanner parses
// (apps/ace/internal/service/layout_db.go). A slide is authored in one of two
// modes:
//
//   Image slide — `image_url` (desktop, the required default) plus optional
//   image_url_tablet/mobile/tv device variants (ACE falls back to desktop) and
//   an optional destination: `link_ref` naming a page, or `link` holding a raw
//   href. A slide with neither renders but does not respond to a click.
//   Plain CDN-URL strings, NOT the tc_image_link flow banner_image uses.
//   content_type/content_id may additionally link the slide to a live match
//   for the derived badge (legacy linkage, no UI).
//
//   Match-card slide — content_type "match" + content_id plus the two hero
//   artwork-template keys (hero_template_desktop / hero_template_mobile). No
//   authored image or link: the render pipeline generates the imagery per
//   lifecycle phase (UPCOMING/LIVE/REPLAY badge baked in) and ACE derives the
//   click-through from the content ref.
//
// size defaults to LARGE when absent. There is no authored badge: ACE derives
// HeroSlide.badge (or the template bakes it), so the CMS doesn't offer one.
export interface HeroSlideMetadata {
  id: string
  /** Switched off by the author: the slide is kept, but neither validated for
   * completeness nor rendered. Absent means it renders — the toggle writes the
   * key only when true, so a slide authored before the toggle is unchanged.
   * Parking an unfinished slide is the point, so the "needs image"/"needs
   * link" rules are skipped here, in the API, and in ACE. */
  disabled?: boolean
  /** Optional half-open schedule, stored as ISO-8601 UTC. Outside the window
   * ACE skips the slide exactly as it skips a disabled one — before the
   * completeness check, so a slide scheduled for next week may still be
   * half-built. Both bounds are independent of `disabled`, which wins: they
   * answer different questions, so parking a slide keeps the schedule it will
   * come back to. Mirrors tc_widget_placement's starts_at/ends_at. */
  starts_at?: string
  ends_at?: string
  image_url: string
  image_url_tablet?: string
  image_url_mobile?: string
  image_url_tv?: string
  /** Image description for screen readers. Sent to the upload endpoint as the
   * tc_image alt text; persisted here so it survives reloads and edits, and
   * served to clients as HeroSlide.altText. */
  alt_text?: string
  /** Raw href — an off-site destination, and every slide authored before the
   * LinkRef picker landed. ACE serves it as-authored. */
  link: string
  /** The click-through as a page reference instead of a raw href: ACE resolves
   * it per request, so a renamed slug self-heals. Mutually exclusive with a
   * non-empty `link` — the picker writes one or the other. */
  link_ref?: HeroSlideLinkRef
  content_type?: string
  content_id?: string
  /** tc_artwork_template keys (template_type 'hero') for generated match-card
   * slides — one per rendered surface (tablet/TV serve the desktop render). */
  hero_template_desktop?: string
  hero_template_mobile?: string
}

/** CMS-only rule (the API and DB don't enforce it): every slide that's on needs alt text. */
const needsAltText = (s: HeroSlideMetadata): boolean => !s.disabled && !s.alt_text?.trim()

/** Why a hero banner can't be saved, or null. Run by the save handlers before they write. */
export const heroBannerSaveError = (metadata: HeroBannerMetadata): string | null => {
  const slides = metadata.slides ?? []
  const missing = slides.flatMap((s, i) => (needsAltText(s) ? [i + 1] : []))
  if (missing.length === 0) return null
  if (missing.length === 1) return `Alt text is missing from slide ${missing[0]}.`
  return `Alt text is missing from slides ${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}.`
}

/** Whether a slide has an authored schedule at all. An unscheduled slide shows
 * no window in the list: "Always" on every row would be noise. */
const isScheduled = (s: HeroSlideMetadata): boolean => !!(s.starts_at || s.ends_at)

/** Whether the slide would render right now — on, and inside its window. This
 * is what the cap counts, mirroring ACE: disabled is checked first, then the
 * window, and both before anything about completeness. */
const isSlideLive = (s: HeroSlideMetadata): boolean =>
  !s.disabled && getScheduleStatus(s.starts_at, s.ends_at).label === 'Live'

/** Match-card mode: authored as a match ref, not an image. Legacy slides with
 * an authored image AND a content link stay image slides. */
const isMatchSlide = (s: HeroSlideMetadata): boolean =>
  s.content_type === 'match' && !s.image_url

/** A slide's stored destination → the picker's value. A page reference is the
 * authored form; a raw `link` loads as an external URL, which is what it is —
 * the picker's external mode accepts both absolute URLs and on-site paths, so
 * slides authored before the picker existed round-trip unchanged. */
const slideLinkRefValue = (slide: HeroSlideMetadata): LinkRefValue => {
  if (slide.link_ref?.page_type) {
    const { page_type, page_id, node_id } = slide.link_ref
    return linkRefFromStored(page_type, page_id, node_id)
  }
  if (slide.link) {
    return { ...EMPTY_LINK_REF, page_type: EXTERNAL_PAGE_TYPE, external_url: slide.link }
  }
  return EMPTY_LINK_REF
}

/** The picker's value → the slide's stored fields. The two destination fields
 * are mutually exclusive, so each mode clears the other: an external link is
 * the raw href in `link`, a page link is the reference in `link_ref`. */
const slideLinkPatch = (v: LinkRefValue): Partial<HeroSlideMetadata> => {
  if (isExternalLinkRef(v)) {
    return { link: v.external_url, link_ref: undefined }
  }
  // An unfinished channel choice stores no link, not a plain Live one.
  const channelPending = v.page_type === CHANNEL_PAGE_TYPE && !v.node_id
  if (!v.page_type || channelPending) return { link: '', link_ref: undefined }
  return {
    link: '',
    link_ref: {
      page_type: storedPageType(v),
      page_id: v.page_id || undefined,
      node_id: v.node_id || undefined,
    },
  }
}

/** The collapsed row's destination line: the href for a raw link, the page's
 * name for a reference — its subject would cost a lookup per slide, and the
 * expanded picker already shows it. */
const slideLinkSummary = (slide: HeroSlideMetadata, types: PageTypeInfo[]): string => {
  if (slide.link) return slide.link
  const code = slide.link_ref?.page_type
  if (!code) return ''
  return types.find(t => t.code === code)?.title ?? code
}

// The four device image slots per slide. `image_url` (desktop) is the required
// default; the rest fall back to it at render time. Keep the type_code-free
// keys in sync with ACE's heroSlideImageURL.
const HERO_IMAGE_SLOTS: { key: keyof HeroSlideMetadata; label: string; hint: string }[] = [
  { key: 'image_url', label: 'Desktop', hint: 'Default' },
  { key: 'image_url_tablet', label: 'Tablet', hint: 'Falls back to desktop' },
  { key: 'image_url_mobile', label: 'Mobile', hint: 'Falls back to desktop' },
  { key: 'image_url_tv', label: 'TV', hint: 'Falls back to desktop' },
]

/** A LinkRef as a hero slide stores it — the CMS's shared LinkRefValue minus
 * the form-only fields, with the inapplicable ids omitted rather than blank.
 * page_type is the field every shape carries; page_id addresses a custom page
 * and node_id a per-entity page's subject. */
export interface HeroSlideLinkRef {
  page_type: string
  page_id?: string
  node_id?: string
}

export interface HeroBannerMetadata {
  size?: 'LARGE' | 'STANDARD'
  rotation_interval_ms?: number
  /** The largest number of slides a client receives. Absent means every
   * enabled slide. ACE applies it after it drops the slides it cannot resolve,
   * so the slides past the cap are bench depth rather than dead weight: one of
   * them renders in place of a slide whose page is unpublished or whose match
   * card has not been generated. That is why the list below marks them rather
   * than hiding them. */
  max_slides?: number
  slides?: HeroSlideMetadata[]
}

interface HeroBannerEditorProps {
  metadata: HeroBannerMetadata
  onChange: (metadata: HeroBannerMetadata) => void
  /** Present once the widget row exists (auto-created on name blur); required to upload slide images. */
  widgetId?: string
}

interface Opt {
  value: string
  label: string
}

// The dimensions are the desktop artwork each size is authored at, and they are
// the ratio the clients render the banner at — so art at the other size's shape
// gets cropped left and right, not letterboxed.
const SIZE_OPTIONS: Opt[] = [
  { value: 'LARGE', label: 'Large — 1920×475 desktop' },
  { value: 'STANDARD', label: 'Standard — 1920×333 desktop' },
]

const MODE_OPTIONS: Opt[] = [
  { value: 'image', label: 'Image' },
  { value: 'match', label: 'Match card (generated)' },
]

/** The generated desktop render for a match slide, as the row thumbnail. The
 * renders live on the match (tc_image_link, purpose = template key), so this
 * reads the match's images and picks the one this slide's template produced.
 * Empty until the pipeline's first successful render lands — same condition
 * under which ACE serves the slide at all. */
const MatchCardThumb: React.FC<{ matchId?: string; templateKey?: string }> = ({
  matchId,
  templateKey,
}) => {
  const { data } = useSWR(
    matchId ? ['match-images', matchId] : null,
    () => fetchMatchImages(matchId!),
  )
  const url = templateKey
    ? data?.images.find(
        (img) => img.type_code === 'hero_card_desktop' && img.purpose === templateKey,
      )?.cdn_url
    : undefined
  return url ? (
    <img src={url} alt="" className={styles.thumb} title="Latest generated card" />
  ) : (
    <span className={styles.thumbEmpty} title="No card generated yet" />
  )
}

/** A match-card slide's destination: the match, on the picker's SWR key. */
const MatchSlideTitle: React.FC<{ matchId?: string }> = ({ matchId }) => {
  const { data: label } = useSWR(
    matchId ? [MATCH_LABEL_SWR_KEY, matchId] : null,
    () => fetchMatchLabel(matchId!),
    { revalidateOnFocus: false },
  )
  return (
    <span className={styles.summaryTitle} title={label}>
      {matchId ? (label ?? 'Match card') : 'No match yet'}
    </span>
  )
}

const HeroBannerEditor: React.FC<HeroBannerEditorProps> = ({ metadata, onChange, widgetId }) => {
  const slides = metadata.slides ?? []
  const { siteId } = useWidgetSite()

  // The page-type vocabulary behind each slide's destination picker. Fetched
  // here too, on the key LinkRefSelector uses, so the completeness badge and
  // the collapsed row's summary can read a destination without waiting for a
  // slide to be expanded. SWR dedupes the two.
  const { data: typeConfig } = useSWR(
    siteId ? ['page-type-config', siteId] : null,
    () => fetchPageTypeConfig(siteId),
    { revalidateOnFocus: false },
  )
  const pageTypes = useMemo<PageTypeInfo[]>(() => typeConfig?.types ?? [], [typeConfig])

  // Hero artwork templates for the match-card dropdowns. The list endpoint
  // has no canvas info, so both dropdowns offer every hero template — the
  // desktop/mobile split lives in the template names. Unpublished templates
  // are flagged: a slide naming one renders nothing until it's published.
  // Focus revalidation stays ON (unlike the templates list page, which
  // refreshes itself after creates): the typical flow is "create a hero
  // template in another tab, come back here and pick it" — without the
  // refetch-on-focus the dropdown serves a pre-creation cache until reload.
  const { data: templatesResp } = useSWR('artwork-templates', fetchArtworkTemplates)
  const heroTemplateOptions = useMemo<Opt[]>(
    () =>
      (templatesResp?.templates ?? [])
        .filter((t) => t.template_type === 'hero' && t.is_active)
        .map((t) => ({
          value: t.key,
          label: `${t.name || t.key}${t.live_version ? '' : ' — unpublished'}`,
        })),
    [templatesResp],
  )

  // Latest metadata for async callbacks (image upload) that would otherwise
  // close over a stale render's slides/index and write to the wrong slide.
  const metadataRef = useRef(metadata)
  metadataRef.current = metadata

  // Which slides are expanded for editing. Collapsed rows are compact and
  // draggable; a newly added slide opens expanded so it can be filled in.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // The destination picker's value per slide, while it is being filled in. A
  // slide stores only a finished destination — a raw href, or a page reference
  // — so a half-made one has nowhere to live: choosing "External URL…" and not
  // yet typing the URL, or clearing the URL to retype it, would round-trip
  // through the slide as "nothing chosen" and snap the picker back to empty.
  const [linkDrafts, setLinkDrafts] = useState<Record<string, LinkRefValue>>({})

  const linkValueFor = (slide: HeroSlideMetadata): LinkRefValue =>
    linkDrafts[slide.id] ?? slideLinkRefValue(slide)

  const setSlideLink = (id: string, v: LinkRefValue) => {
    setLinkDrafts(prev => ({ ...prev, [id]: v }))
    patchSlideById(id, slideLinkPatch(v))
  }

  // Dropping the draft is what makes the slide the source of truth again —
  // used when the slide's destination changes out from under the picker.
  const clearLinkDraft = useCallback((id: string) => {
    setLinkDrafts(prev => {
      if (!(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const update = useCallback(
    (patch: Partial<HeroBannerMetadata>) => onChange({ ...metadataRef.current, ...patch }),
    [onChange],
  )

  const commitSlides = useCallback(
    (next: HeroSlideMetadata[]) => update({ slides: next }),
    [update],
  )

  // Patch a slide by id off the latest metadata. Use this from async callbacks
  // (upload) so a reorder/edit in flight can't misroute the result.
  const patchSlideById = useCallback(
    (id: string, patch: Partial<HeroSlideMetadata>) => {
      const cur = metadataRef.current.slides ?? []
      commitSlides(cur.map(s => (s.id === id ? { ...s, ...patch } : s)))
    },
    [commitSlides],
  )

  // Switching mode clears the other mode's fields so a slide is never both:
  // an image alongside a match ref would make ACE serve the stale image while
  // the pipeline renders cards nobody displays.
  const setSlideMode = useCallback(
    (id: string, mode: 'image' | 'match') => {
      // Match mode has no authored destination and image mode starts without
      // one, so neither keeps the picker's in-progress value.
      clearLinkDraft(id)
      if (mode === 'match') {
        patchSlideById(id, {
          image_url: '',
          image_url_tablet: undefined,
          image_url_mobile: undefined,
          image_url_tv: undefined,
          link: '',
          link_ref: undefined,
          content_type: 'match',
        })
      } else {
        patchSlideById(id, {
          content_type: undefined,
          content_id: undefined,
          hero_template_desktop: undefined,
          hero_template_mobile: undefined,
        })
      }
    },
    [patchSlideById, clearLinkDraft],
  )

  const { dragIndex, handleProps, rowProps } = useDragReorder(
    useCallback(
      (from: number, to: number) => commitSlides(arrayMove(metadataRef.current.slides ?? [], from, to)),
      [commitSlides],
    ),
    { dragImageFromParent: true },
  )

  const toggleExpanded = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const addSlide = () => {
    const id = crypto.randomUUID()
    commitSlides([...slides, { id, image_url: '', link: '' }])
    setExpanded(prev => new Set(prev).add(id))
  }

  // Switching a slide off never moves it. Re-enabling has to put it back where
  // it was, and a toggle that silently reorders a drag-ordered list is a
  // surprise, so the stored order is left alone and the row is dimmed in place.
  const toggleDisabled = (id: string) => {
    const cur = (metadataRef.current.slides ?? []).find(s => s.id === id)
    // Written only when true, so an enabled slide carries no key at all.
    patchSlideById(id, { disabled: cur?.disabled ? undefined : true })
  }

  // The schedule editor works in Pacific-time datetime-local strings; the
  // metadata stores ISO-8601 UTC, the form ACE parses. Blank clears the bound
  // rather than storing an empty string.
  const setSlideBound = (id: string, key: 'starts_at' | 'ends_at', ptValue: string) => {
    patchSlideById(id, { [key]: ptValue ? convertPTInputToUTC(ptValue) : undefined })
  }

  const removeSlide = (id: string) => {
    commitSlides((metadataRef.current.slides ?? []).filter(s => s.id !== id))
    setExpanded(prev => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const sizeValue = SIZE_OPTIONS.find(o => o.value === (metadata.size ?? 'LARGE')) ?? SIZE_OPTIONS[0]

  // The three counts an author needs: how many slides exist, how many are on,
  // and how many of those the cap lets through. They are shown together
  // because the row numbering counts authored slides, so it cannot carry any
  // of this on its own. renderCount is an upper bound — ACE drops what it
  // cannot resolve, and a benched slide then takes the free place.
  // "on" is "not switched off"; "render" counts the slides inside their window
  // right now, so a slide scheduled for next week is on but does not render.
  const enabledCount = slides.filter(s => !s.disabled).length
  const liveCount = slides.filter(isSlideLive).length
  // A cap below 1 is not a cap. The schema refuses it, but the field holds
  // whatever is typed until then, and a literal 0 would otherwise read as
  // "every slide is past the cap" while the counts below ignored it.
  const cap = metadata.max_slides && metadata.max_slides > 0 ? metadata.max_slides : undefined
  const renderCount = cap ? Math.min(liveCount, cap) : liveCount
  const slideCountSummary =
    renderCount === slides.length
      ? `${slides.length}`
      : `${slides.length} · ${enabledCount} on · ${renderCount} render`

  // Each rendering slide's place in the render queue, which is what the cap
  // counts. Slides that are off or outside their window are absent from the
  // map: they hold their position in the list and their "Slide N" number, but
  // they take no place in the queue, so switching one off — or letting its
  // window close — promotes everything below it.
  const renderOrdinal = new Map<string, number>()
  slides.forEach((s) => {
    if (isSlideLive(s)) renderOrdinal.set(s.id, renderOrdinal.size + 1)
  })

  return (
    <>
      <div className={formStyles.formGrid}>
        <div className={formStyles.fieldGroup}>
          <label title="The banner's height, and the shape of the desktop artwork it expects: Large is 1920×475, Standard is 1920×333.">Size</label>
          <Select
            value={sizeValue}
            onChange={(opt) => opt && update({ size: opt.value as HeroBannerMetadata['size'] })}
            options={SIZE_OPTIONS}
            className={selectStyles.reactSelect}
            classNamePrefix="react-select"
          />
        </div>

        <div className={formStyles.fieldGroup}>
          <label title="How long each slide shows before rotating to the next.">Rotation interval (ms)</label>
          <input
            type="number"
            min={0}
            placeholder="e.g. 6000 — leave blank to not auto-rotate"
            value={metadata.rotation_interval_ms ?? ''}
            onChange={(e) =>
              update({ rotation_interval_ms: e.target.value ? parseInt(e.target.value, 10) : undefined })
            }
          />
        </div>

        <div className={formStyles.fieldGroup}>
          <label title="Caps how many slides a client receives. Applied to the slides that actually render, so a slide past the cap takes the place of one that cannot be resolved.">
            Max rendered slides
          </label>
          <input
            type="number"
            min={1}
            placeholder="Leave blank to render every slide"
            value={metadata.max_slides ?? ''}
            onChange={(e) =>
              update({ max_slides: e.target.value ? parseInt(e.target.value, 10) : undefined })
            }
          />
        </div>
      </div>

      <div className={styles.slidesHeader}>
        <span>Slides</span>
        <Badge variant="secondary" size="small">{slideCountSummary}</Badge>
      </div>

      {!widgetId && (
        <Banner variant="info" message="Name the widget above to unlock slide image uploads." />
      )}

      {slides.length > 0 && enabledCount === 0 && (
        <Banner
          variant="warning"
          message="Every slide is switched off. A hero banner needs at least one slide on before it can be saved."
        />
      )}

      {enabledCount > 0 && liveCount === 0 && (
        <Banner
          variant="warning"
          message="No slide is inside its schedule right now, so the banner renders as an error until one opens. This still saves — a banner scheduled to start later is a normal thing to author."
        />
      )}

      {slides.length === 0 && (
        <p className={formStyles.hint}>No slides yet — a hero banner needs at least one.</p>
      )}

      {slides.map((slide, i) => {
        const isOpen = expanded.has(slide.id)
        const matchMode = isMatchSlide(slide)
        const off = !!slide.disabled
        const ordinal = renderOrdinal.get(slide.id)
        // Past the cap: on, but only rendered if a slide above it drops.
        const benched = ordinal !== undefined && cap !== undefined && ordinal > cap
        // The cap falls after this row, and there is something below it to cut
        // off. Drawn between the rows rather than as a per-row border so it
        // reads as one line across the list.
        const cutoffAfter =
          ordinal !== undefined && cap !== undefined && ordinal === cap && liveCount > cap
        // Mirrors the API's hero validation: an image slide needs an image; a
        // match-card slide needs a match and both template picks. The link is
        // optional, so only a destination that is started but not finished
        // (an external URL not yet typed, a channel not yet chosen) is missing.
        // Alt text is the CMS-only rule on top, for both modes.
        const missing = (matchMode
          ? !slide.content_id
            ? 'match'
            : !slide.hero_template_desktop || !slide.hero_template_mobile
              ? 'templates'
              : null
          : !slide.image_url
            ? 'image'
            : linkValueFor(slide).page_type && validateLinkRef(linkValueFor(slide), pageTypes)
              ? 'link'
              : null) ?? (needsAltText(slide) ? 'alt text' : null)
        const linkSummary = slideLinkSummary(slide, pageTypes)
        // The words the placements list uses for a widget's schedule
        // (getScheduleDisplay / getPlacementStatus), one level down.
        const scheduled = isScheduled(slide)
        const scheduleRange = scheduled ? formatScheduleRange(slide.starts_at, slide.ends_at) : ''
        const scheduleStatus = scheduled ? getScheduleStatus(slide.starts_at, slide.ends_at) : null
        // A parked slide shows no status badge; its window stays in the
        // summary line as the plan it comes back to.
        const statusBadge = !off && scheduleStatus && scheduleStatus.label !== 'Live' ? scheduleStatus : null
        return (
          <React.Fragment key={slide.id}>
            <div
              className={`${styles.slide} ${dragIndex === i ? styles.dragging : ''} ${off ? styles.slideOff : ''}`}
              {...rowProps(i)}
            >
              <div className={styles.slideRow}>
                <span
                  className={styles.handle}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                  {...handleProps(i)}
                >
                  ⠿
                </span>

                {matchMode ? (
                  <MatchCardThumb
                    matchId={slide.content_id}
                    templateKey={slide.hero_template_desktop}
                  />
                ) : slide.image_url ? (
                  <img src={slide.image_url} alt="" className={styles.thumb} />
                ) : (
                  <span className={styles.thumbEmpty} />
                )}

                <button type="button" className={styles.summary} onClick={() => toggleExpanded(slide.id)}>
                  <span className={styles.summaryIndex}>
                    {`Slide ${i + 1}`}{matchMode && ' · Match card'}
                  </span>
                  {matchMode ? (
                    <MatchSlideTitle matchId={slide.content_id} />
                  ) : (
                    <span className={styles.summaryTitle} title={linkSummary}>
                      {linkSummary || 'No link'}
                    </span>
                  )}
                  {scheduleRange && (
                    <span className={styles.summarySchedule}>{scheduleRange}</span>
                  )}
                </button>

                {/* An off slide still shows what it is missing. The exemption is
                    from being refused at save, not from being told: an author who
                    switches a parked slide back on would otherwise meet the error
                    only when the save fails. */}
                <div className={styles.rowActions}>
                  {/* Rendered only when there is a badge to show, so the run's
                      gap never opens a space around nothing. */}
                  {(statusBadge || benched || missing) && (
                    <div className={styles.rowBadges}>
                      {statusBadge && (
                        <span title={statusBadge.label === 'Scheduled'
                          ? 'Inside the list, but not rendering until its window opens.'
                          : 'Its window has closed, so it no longer renders.'}>
                          <Badge variant={statusBadge.variant} size="small">{statusBadge.label}</Badge>
                        </span>
                      )}
                      {benched && (
                        <span title={`Past the cap of ${cap}. It renders if a slide above it cannot be resolved.`}>
                          <Badge variant="secondary" size="small">Bench</Badge>
                        </span>
                      )}
                      {missing && (
                        <Badge variant="warning" size="small">Needs {missing}</Badge>
                      )}
                    </div>
                  )}
                  <ToggleGroup
                    variant="toggle"
                    size="small"
                    // ToggleGroup's default active fill is $tc-accent, the
                    // same green as 'live', which would paint a switched-off
                    // slide the colour of a working one.
                    options={[
                      { value: 'on', label: 'On', color: 'live' },
                      { value: 'off', label: 'Off', color: 'ended' },
                    ]}
                    value={off ? 'off' : 'on'}
                    onChange={(v) => {
                      if ((v === 'off') !== off) toggleDisabled(slide.id)
                    }}
                  />
                  <button
                    type="button"
                    className={`${styles.rowIconBtn} ${styles.trashBtn}`}
                    onClick={() => removeSlide(slide.id)}
                    title="Remove slide"
                    aria-label="Remove slide"
                  >
                    <TrashIcon />
                  </button>
                  <span className={styles.actionsDivider} aria-hidden="true" />
                  <button
                    type="button"
                    className={`${styles.rowIconBtn} ${styles.chevronBtn} ${isOpen ? styles.chevronOpen : ''}`}
                    onClick={() => toggleExpanded(slide.id)}
                    title={isOpen ? 'Collapse' : 'Edit'}
                    aria-label={isOpen ? 'Collapse slide' : 'Edit slide'}
                    aria-expanded={isOpen}
                  >
                    <ChevronIcon />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className={styles.slideBody}>
                  <div className={formStyles.fieldGroup}>
                    <label title="Image slides use uploaded artwork; match-card slides are generated from a match and hero templates, and regenerate as the match's state changes.">
                      Slide type
                    </label>
                    <Select
                      value={MODE_OPTIONS[matchMode ? 1 : 0]}
                      onChange={(opt) =>
                        opt && setSlideMode(slide.id, opt.value as 'image' | 'match')
                      }
                      options={MODE_OPTIONS}
                      className={selectStyles.reactSelect}
                      classNamePrefix="react-select"
                    />
                  </div>

                  {matchMode ? (
                    <>
                      <div className={formStyles.fieldGroup}>
                        <label>Match</label>
                        <MatchSelect
                          value={slide.content_id ?? ''}
                          onChange={(id) =>
                            patchSlideById(slide.id, { content_id: id || undefined })
                          }
                        />
                        <small className={formStyles.hint}>{MATCH_SEARCH_HINT}</small>
                      </div>

                      <div className={formStyles.fieldGroup}>
                        <label>Desktop template</label>
                        <Select
                          value={
                            heroTemplateOptions.find(
                              (o) => o.value === slide.hero_template_desktop,
                            ) ??
                            (slide.hero_template_desktop
                              ? { value: slide.hero_template_desktop, label: slide.hero_template_desktop }
                              : null)
                          }
                          onChange={(opt) =>
                            patchSlideById(slide.id, {
                              hero_template_desktop: opt?.value || undefined,
                            })
                          }
                          options={heroTemplateOptions}
                          isClearable
                          placeholder="Pick a 1920×475 hero template…"
                          noOptionsMessage={() => 'No hero templates yet — create one under Artwork Templates'}
                          className={selectStyles.reactSelect}
                          classNamePrefix="react-select"
                        />
                      </div>

                      <div className={formStyles.fieldGroup}>
                        <label>Mobile template</label>
                        <Select
                          value={
                            heroTemplateOptions.find(
                              (o) => o.value === slide.hero_template_mobile,
                            ) ??
                            (slide.hero_template_mobile
                              ? { value: slide.hero_template_mobile, label: slide.hero_template_mobile }
                              : null)
                          }
                          onChange={(opt) =>
                            patchSlideById(slide.id, {
                              hero_template_mobile: opt?.value || undefined,
                            })
                          }
                          options={heroTemplateOptions}
                          isClearable
                          placeholder="Pick a 4:3 hero template…"
                          noOptionsMessage={() => 'No hero templates yet — create one under Artwork Templates'}
                          className={selectStyles.reactSelect}
                          classNamePrefix="react-select"
                        />
                      </div>

                      <p className={formStyles.hint}>
                        The card image is generated automatically and refreshes as
                        the match moves through upcoming, live, and replay. The
                        click-through follows the match, so there’s no link to
                        set.
                      </p>
                    </>
                  ) : (
                    <div className={styles.imageSlots}>
                      {HERO_IMAGE_SLOTS.map((sl) => {
                        const url = (slide[sl.key] as string | undefined) || ''
                        return (
                          <ImageSlot
                            key={sl.key}
                            label={sl.label}
                            hint={sl.hint}
                            aspectRatio="16 / 9"
                            disabled={!widgetId}
                            image={url ? { url } : null}
                            onUpload={async (file) => {
                              const { cdn_url } = await uploadHeroImage(widgetId!, file, slide.alt_text)
                              patchSlideById(slide.id, { [sl.key]: cdn_url } as Partial<HeroSlideMetadata>)
                            }}
                            onRemove={url ? () => patchSlideById(slide.id, { [sl.key]: '' } as Partial<HeroSlideMetadata>) : undefined}
                          />
                        )
                      })}
                    </div>
                  )}

                  <div className={formStyles.fieldGroup}>
                    <label>Alt text <Badge variant="secondary" size="small">Public</Badge></label>
                    <input
                      type="text"
                      required={!off}
                      value={slide.alt_text ?? ''}
                      onChange={(e) => patchSlideById(slide.id, { alt_text: e.target.value || undefined })}
                      placeholder="Describe the image for screen readers"
                    />
                  </div>

                  {!matchMode && (
                    <LinkRefSelector
                      siteId={siteId}
                      value={linkValueFor(slide)}
                      onChange={(v) => setSlideLink(slide.id, v)}
                      allowExternal
                      optional
                    />
                  )}

                  <ScheduleFields
                    startsAt={slide.starts_at ? convertUTCToPTInput(slide.starts_at) : ''}
                    onStartsAtChange={(v) => setSlideBound(slide.id, 'starts_at', v)}
                    endsAt={slide.ends_at ? convertUTCToPTInput(slide.ends_at) : ''}
                    onEndsAtChange={(v) => setSlideBound(slide.id, 'ends_at', v)}
                    // A slide keeps no separate "has an end date" flag: the
                    // stored bound is the whole state, so clearing the input
                    // is how an author removes the end.
                    hasEnd={!!slide.ends_at}
                    onHasEndChange={(has) => {
                      if (!has) setSlideBound(slide.id, 'ends_at', '')
                    }}
                  />
                  <small className={formStyles.hint}>
                    Outside its window a slide sits out, and the ones below it
                    move up. Switching a slide off overrides its schedule
                    without clearing it.
                  </small>
                </div>
              )}
            </div>
            {cutoffAfter && (
              <div className={styles.renderCutoff}>
                <span>Renders above this line</span>
              </div>
            )}
          </React.Fragment>
        )
      })}

      <button type="button" onClick={addSlide} className={styles.addSlide}>
        + Add slide
      </button>
    </>
  )
}

export default HeroBannerEditor
